import React from "react";

function createEmptyLine(nextNo = 1) {
  return {
    line_no: nextNo,
    resource_item_id: "",
    asset_unit_id: "",
    description: "",
    qty: 1,
    uom_id: "",
    unit_price: 0,
    amount: 0,
    currency: "RSD",
    project_id: "",
    employee_id: "",
    cost_center_id: "",
    source_location_id: "",
    target_location_id: "",
  };
}

export default function DocumentLinesTable({
  lines = [],
  onChange,
  resourceItems = [],
  uoms = [],
  inventoryMap = {},
  header = {},
  disabled = false,
}) {
  const getAvailableQty = (line) => {
    if (!line.resource_item_id) return 0;

    const key = `${line.resource_item_id}_${header.source_warehouse_id}_${header.source_location_id}`;
    return Number(inventoryMap[key] || 0);
  };

  const setLineField = (index, field, value) => {
    if (disabled) return;

    const next = [...lines];
    next[index] = {
      ...next[index],
      [field]: value,
    };

    if (field === "qty" || field === "unit_price") {
      const qty = Number(next[index].qty || 0);
      const price = Number(next[index].unit_price || 0);
      next[index].amount = qty * price;
    }

    if (field === "resource_item_id") {
      const item = resourceItems.find((r) => Number(r.id) === Number(value));

      if (item?.uom_id) {
        next[index].uom_id = item.uom_id;
      }

      if (!next[index].description && item?.name) {
        next[index].description = item.name;
      }

      if (item?.default_issue_price != null) {
        next[index].unit_price = Number(item.default_issue_price || 0);
        next[index].amount =
          Number(next[index].qty || 0) * Number(next[index].unit_price || 0);
      }
    }

    onChange(next);
  };

  const addLine = () => {
    if (disabled) return;
    onChange([...lines, createEmptyLine(lines.length + 1)]);
  };

  const removeLine = (index) => {
    if (disabled) return;

    const next = lines.filter((_, i) => i !== index).map((line, idx) => ({
      ...line,
      line_no: idx + 1,
    }));
    onChange(next);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border p-4 mt-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Document Lines</h3>

        {!disabled ? (
          <button
            type="button"
            onClick={addLine}
            className="px-4 py-2 rounded-lg bg-black text-white"
          >
            Add Line
          </button>
        ) : (
          <span className="text-sm text-slate-400">Document locked</span>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="bg-slate-100 text-left">
              <th className="border px-2 py-2">#</th>
              <th className="border px-2 py-2 min-w-[260px]">Resource</th>
              <th className="border px-2 py-2 min-w-[180px]">Description</th>
              <th className="border px-2 py-2 min-w-[110px]">Qty</th>
              <th className="border px-2 py-2 min-w-[120px]">UOM</th>
              <th className="border px-2 py-2 min-w-[120px]">Unit Price</th>
              <th className="border px-2 py-2 min-w-[140px]">Amount</th>
              <th className="border px-2 py-2 min-w-[120px]">Available</th>
              <th className="border px-2 py-2 min-w-[90px]">Action</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={9} className="border px-3 py-4 text-center text-slate-500">
                  No lines yet.
                </td>
              </tr>
            ) : (
              lines.map((line, index) => (
                <tr
                  key={index}
                  className={
                    getAvailableQty(line) < Number(line.qty || 0)
                      ? "bg-red-50"
                      : ""
                  }
                >
                  <td className="border px-2 py-2">{line.line_no || index + 1}</td>

                  <td className="border px-2 py-2">
                    <select
                      className="w-full border rounded-lg px-2 py-2 disabled:bg-slate-100 disabled:text-slate-500"
                      value={line.resource_item_id || ""}
                      disabled={disabled}
                      onChange={(e) =>
                        setLineField(
                          index,
                          "resource_item_id",
                          e.target.value ? Number(e.target.value) : ""
                        )
                      }
                    >
                      <option value="">Select resource</option>
                      {resourceItems.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.sku} - {item.name}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td className="border px-2 py-2">
                    <input
                      type="text"
                      className="w-full border rounded-lg px-2 py-2 disabled:bg-slate-100 disabled:text-slate-500"
                      value={line.description || ""}
                      disabled={disabled}
                      onChange={(e) =>
                        setLineField(index, "description", e.target.value)
                      }
                    />
                  </td>

                  <td className="border px-2 py-2">
                    <input
                      type="number"
                      step="0.0001"
                      className="w-full border rounded-lg px-2 py-2 disabled:bg-slate-100 disabled:text-slate-500"
                      value={line.qty ?? ""}
                      disabled={disabled}
                      onChange={(e) =>
                        setLineField(index, "qty", Number(e.target.value))
                      }
                    />
                    {getAvailableQty(line) < Number(line.qty || 0) && (
                      <div className="text-red-600 text-xs mt-1">
                        ⚠ Not enough stock
                      </div>
                    )}
                  </td>

                  <td className="border px-2 py-2">
                    <select
                      className="w-full border rounded-lg px-2 py-2 disabled:bg-slate-100 disabled:text-slate-500"
                      value={line.uom_id || ""}
                      disabled={disabled}
                      onChange={(e) =>
                        setLineField(
                          index,
                          "uom_id",
                          e.target.value ? Number(e.target.value) : ""
                        )
                      }
                    >
                      <option value="">Select UOM</option>
                      {uoms.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.code} - {u.name}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td className="border px-2 py-2">
                    <input
                      type="number"
                      step="0.0001"
                      className="w-full border rounded-lg px-2 py-2 disabled:bg-slate-100 disabled:text-slate-500"
                      value={line.unit_price ?? ""}
                      disabled={disabled}
                      onChange={(e) =>
                        setLineField(index, "unit_price", Number(e.target.value))
                      }
                    />
                  </td>

                  <td className="border px-2 py-2">
                    <input
                      type="number"
                      step="0.0001"
                      className="w-full border rounded-lg px-2 py-2 bg-slate-50"
                      value={line.amount ?? 0}
                      readOnly
                    />
                  </td>

                  <td className="border px-2 py-2 text-right">
                    {getAvailableQty(line).toFixed(4)}
                  </td>

                  <td className="border px-2 py-2">
                    {!disabled ? (
                      <button
                        type="button"
                        onClick={() => removeLine(index)}
                        className="px-3 py-2 rounded-lg border text-red-600"
                      >
                        Remove
                      </button>
                    ) : (
                      <span className="text-sm text-slate-400">Locked</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}